"use client";

import CoverflowCarousel, {
  type CarouselItem,
} from "@/components/organisms/carousels/CoverflowCarousel";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { formatBackgroundStyle } from "@/utils/styles/format";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";
import {
  type CSSProperties,
  type FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as z from "@/lib/propSchema";

const CARD_GAP_PX = 20;
const CARD_GAP_XL = 40;
const CARD_WIDTH_XS = 335;
const SCROLL_STEP_XS = CARD_WIDTH_XS + CARD_GAP_PX;
const CARD_WIDTH_SM = 238;
const SCROLL_STEP_SM = CARD_WIDTH_SM + CARD_GAP_PX;
const CARD_WIDTH_MD = 280;
const SCROLL_STEP_MD = CARD_WIDTH_MD + CARD_GAP_PX;
const CARD_WIDTH_LG = 320;
const SCROLL_STEP_LG = CARD_WIDTH_LG + CARD_GAP_PX;
const SCROLL_STEP_XL = CARD_WIDTH_LG + CARD_GAP_XL;
const PROGRESS_TRACK_PX_XS = 207;
const PROGRESS_TRACK_PX_SM = 248;
const PROGRESS_TRACK_PX_WIDE = 290;
const PROGRESS_THUMB_PX = 61;
const PROGRESS_THUMB_PX_XS = 44;

const VisionariesProfileSchema = z.object({
  id: z.union([z.number(), z.string()]).optional(),
  category_id: z.union([z.number(), z.string()]).nullable().optional(),
  priority: z.coerce.number().optional().default(1),
  desktop_image: z.string().optional(),
  mobile_image: z.string().optional(),
  image_alt: z.string().optional(),
  profile_name: z.string().optional(),
  profile_title: z.string().optional(),
  profile_name_color: z.string().optional(),
  profile_title_color: z.string().optional(),
  card_overlay_gradient_start: z.string().optional(),
  card_overlay_gradient_end: z.string().optional(),
  card_overlay_gradient_angle: z.string().optional(),
  logo_1: z.string().optional(),
  logo_1_alt: z.string().optional(),
  logo_2: z.string().optional(),
  logo_2_alt: z.string().optional(),
  logos_layout: z.string().optional(),
  /** Optional label shown in coverflow card footer (e.g. company). */
  company_name: z.string().optional(),
  /** Optional subtitle line in coverflow cards. */
  profile_tagline: z.string().optional(),
  is_active: z.union([z.boolean(), z.number()]).optional(),
});

const VisionariesCategorySchema = z.object({
  id: z.union([z.number(), z.string()]).optional(),
  name: z.string().optional(),
  text_color: z.string().optional(),
  border_color: z.string().optional(),
  background_color: z.string().optional(),
  active_text_color: z.string().optional(),
  active_background_color: z.string().optional(),
  active_border_color: z.string().optional(),
  order_index: z.coerce.number().optional().default(0),
  is_active: z.union([z.boolean(), z.number()]).optional(),
  is_default: z.union([z.boolean(), z.number()]).optional(),
  is_uncategorized_bucket: z.boolean().optional(),
  profiles: z.array(VisionariesProfileSchema).optional(),
});

const Visionaries1DataSchema = z.object({
  id: z.union([z.number(), z.string()]).optional(),
  status: z.union([z.boolean(), z.number()]).optional(),
  pre_title: z.string().optional(),
  pre_title_color: z.string().optional(),
  title: z.string().optional(),
  title_start_color: z.string().optional(),
  title_end_color: z.string().optional(),
  title_gradient_angle: z.string().optional(),
  desktop_background_color: z.string().optional(),
  mobile_background_color: z.string().optional(),
  backvariant: z
    .string()
    .optional()
    .transform((v) => {
      const x = (v ?? "variant_1").trim();
      if (x === "variant_2" || x === "variant_3" || x === "variant_1") return x;
      return "variant_1";
    }),
  gradient_color_1: z.string().optional(),
  gradient_color_2: z.string().optional(),
  gradient_color_3: z.string().optional(),
  gradient_color_4: z.string().optional(),
  left_navigation_icon: z.string().optional(),
  right_navigation_icon: z.string().optional(),
  progress_track_color: z.string().optional(),
  progress_fill_color: z.string().optional(),
  /** When false, cards are shown without category tabs (direct list). Default true. */
  use_categories: z
    .union([z.boolean(), z.number()])
    .optional()
    .transform((v) => !(v === false || v === 0)),
  enable_animation: z
    .union([z.boolean(), z.number(), z.string()])
    .optional()
    .transform((v) =>
      v === undefined ? false : v === true || v === 1 || v === "1",
    ),
  /** When false, use legacy horizontal strip with arrows and progress bar. Default true (coverflow). */
  enable_coverflow_carousel: z
    .union([z.boolean(), z.number(), z.string()])
    .optional()
    .transform((v) =>
      v === undefined ? true : !(v === false || v === 0 || v === "0"),
    ),
  categories: z.array(VisionariesCategorySchema).optional().default([]),
  pool_profiles: z.array(VisionariesProfileSchema).optional().default([]),
  description: z.string().optional(),
  description_color: z.string().optional(),
  /** CoverflowCarousel: autoplay (default off). */
  coverflow_autoplay: z
    .union([z.boolean(), z.number(), z.string()])
    .optional()
    .transform((v) => v === true || v === 1 || v === "1"),
  /** CoverflowCarousel autoplay interval in ms (default 4000). */
  coverflow_autoplay_delay: z.coerce.number().optional(),
  /** CoverflowCarousel Embla loop; omit or true for loop (default). */
  coverflow_loop: z
    .union([z.boolean(), z.number(), z.string()])
    .optional()
    .transform((v) =>
      v === undefined ? undefined : !(v === false || v === 0 || v === "0"),
    ),
});

type VisionariesProfile = z.infer<typeof VisionariesProfileSchema>;
type VisionariesCategory = z.infer<typeof VisionariesCategorySchema>;

type LayoutTier = "base" | "sm" | "md" | "lg" | "xl";

function getLayoutTier(width: number): LayoutTier {
  if (width >= 1750) return "xl";
  if (width >= 1430) return "lg";
  if (width >= 1230) return "md";
  if (width >= 1080) return "sm";
  return "base";
}

function resolveActiveTabTextColor(raw?: string): string {
  const v = (raw || "#FFFFFF").trim();
  if (v.toLowerCase() === "#0a0a0a") return "#FFFFFF";
  return v;
}

function normalizeProfiles(
  raw: VisionariesCategory | undefined
): VisionariesProfile[] {
  if (!raw) return [];
  const list = raw.profiles ?? [];
  return list
    .filter((p) => p.is_active !== false && p.is_active !== 0)
    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
}

const ProfileCard: FC<{
  profile: VisionariesProfile;
  imageUrl: string;
  className?: string;
}> = ({ profile, imageUrl, className }) => {
  const overlayAngle = profile.card_overlay_gradient_angle || "180deg";
  const overlayStart = profile.card_overlay_gradient_start ?? "#000000";
  const overlayEnd = profile.card_overlay_gradient_end ?? "#00000000";
  const overlayStyle: CSSProperties = {
    backgroundImage: `linear-gradient(${overlayAngle}, ${overlayStart}, ${overlayEnd})`,
  };

  const logosRow = profile.logos_layout === "stack";
  const hasLogo1 = profile.logo_1 && profile.logo_1.trim() !== "";
  const hasLogo2 = profile.logo_2 && profile.logo_2.trim() !== "";

  return (
    <article
      className={cn(
        "relative shrink-0 overflow-hidden bg-neutral-900",
        "max-[1079px]:aspect-[335/410] max-[1079px]:h-auto max-[1079px]:min-h-0",
        "max-[1079px]:w-full max-[1079px]:min-w-full max-[1079px]:max-w-full",
        "max-[1079px]:rounded-[20px]",
        "min-[1080px]:h-[340px] min-[1080px]:w-[238px] min-[1080px]:min-w-[238px] min-[1080px]:max-w-none min-[1080px]:rounded-[17.5px]",
        "min-[1230px]:h-[400px] min-[1230px]:w-[280px] min-[1230px]:min-w-[280px] min-[1230px]:rounded-[17.5px]",
        "min-[1430px]:h-[467px] min-[1430px]:w-[320px] min-[1430px]:min-w-[320px] min-[1430px]:rounded-[20px]",
        className
      )}
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden max-[1079px]:rounded-[20px] min-[1080px]:rounded-[17.5px] min-[1230px]:rounded-[17.5px] min-[1430px]:rounded-[20px]">
        {imageUrl ? (
          <Image
            src={imageUrl}
            alt={profile.image_alt || profile.profile_name || ""}
            fill
            className="object-cover object-center"
            sizes="(min-width: 1430px) 320px, (min-width: 1230px) 280px, (min-width: 1080px) 238px, (max-width: 1079px) 100vw, 90vw"
          />
        ) : (
          <div className="h-full w-full bg-neutral-800" />
        )}
      </div>
      <div
        className={cn(
          "pointer-events-none absolute inset-x-0 bottom-0 block min-[1080px]:hidden",
          "h-[min(163px,42%)] min-h-[120px]",
          "backdrop-blur-[5px]",
          "backdrop-saturate-[70%]",
          "backdrop-brightness-90",
          "[mask-image:linear-gradient(to_top,black_0%,black_65%,rgba(0,0,0,0.2)_90%,transparent_97%)]"
        )}
        style={overlayStyle}
        aria-hidden
      />
      <div
        className={cn(
          "pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2",
          "hidden min-[1080px]:block",
          "h-[120px] w-full max-w-[min(100%,320px)]",
          "min-[1080px]:w-[238px] min-[1080px]:max-w-none",
          "min-[1230px]:h-[140px] min-[1230px]:w-[280px]",
          "min-[1430px]:h-[163px] min-[1430px]:w-[320px]",
          "backdrop-blur-[5px]",
          "backdrop-saturate-[70%]",
          "backdrop-brightness-90",
          "[mask-image:linear-gradient(to_top,black_0%,black_65%,rgba(0,0,0,0.2)_90%,transparent_97%)]"
        )}
        style={overlayStyle}
      />
      <div
        className={cn(
          "absolute bottom-0 left-1/2 flex w-full max-w-[238px] -translate-x-1/2 flex-col items-center gap-2 px-3 pb-6 pt-0",
          "max-[1079px]:max-w-full max-[1079px]:gap-2.5 max-[1079px]:px-6 max-[1079px]:pb-6 max-[1079px]:pt-7 max-[1079px]:items-stretch",
          "min-[1080px]:w-[238px] min-[1080px]:pb-6",
          "min-[1230px]:max-w-[280px] min-[1230px]:w-[280px] min-[1230px]:gap-2 min-[1230px]:pb-5",
          "min-[1430px]:max-w-[320px] min-[1430px]:w-[320px] min-[1430px]:gap-2.5 min-[1430px]:pb-[34px]"
        )}
      >
        <div className="flex w-full max-w-full flex-col items-center gap-2 min-[1430px]:gap-2.5 max-[1079px]:gap-2.5">
          <p
            className={cn(
              "text-center text-sm font-semibold leading-[1.4] tracking-[0.14px] text-white",
              "max-[1079px]:text-2xl max-[1079px]:leading-[35px] max-[1079px]:tracking-[0.24px] max-[1079px]:w-full max-[1079px]:min-w-0 max-[1079px]:truncate",
              "min-[1080px]:text-base min-[1080px]:tracking-[0.16px]",
              "min-[1230px]:text-[22px] min-[1230px]:tracking-[0.22px]",
              "min-[1430px]:text-[26px] min-[1750px]:tracking-[0.26px]",
              "min-[1430px]:w-full min-[1430px]:min-w-0 min-[1430px]:truncate"
            )}
            style={{ color: profile.profile_name_color || "#FFFFFF" }}
          >
            {profile.profile_name || ""}
          </p>
          <div
            className="h-px w-[30.625px] shrink-0 rounded-full bg-white/80 max-[1079px]:w-[35px] min-[1430px]:w-[35px]"
            aria-hidden
          />
          <div className="flex w-full flex-col items-center gap-1 min-[1430px]:gap-[5px]">
            <p
              className="w-full min-w-0 text-center text-[10px] font-medium italic leading-[1.4] tracking-[0.1px] text-white/80 max-[1079px]:text-xs max-[1079px]:tracking-[0.12px] min-[1430px]:text-xs min-[1750px]:tracking-[0.12px]"
              style={{ color: profile.profile_title_color || "rgba(255,255,255,0.8)" }}
            >
              {profile.profile_title || ""}
            </p>
            <div
              className={cn(
                "flex w-full items-center justify-center gap-2",
                logosRow ? "flex-col" : "flex-row"
              )}
            >
              {hasLogo1 && (
                <div className="relative h-[25px] w-[88px] max-[1079px]:h-[34px] max-[1079px]:w-full max-[1079px]:max-w-[121px] min-[1230px]:h-[28px] min-[1230px]:w-[100px] min-[1430px]:aspect-[940/268] min-[1430px]:h-auto min-[1430px]:w-full min-[1430px]:max-h-[34px]">
                  <Image
                    src={profile.logo_1!}
                    alt={profile.logo_1_alt || ""}
                    fill
                    className="object-contain"
                  />
                </div>
              )}
              {hasLogo2 && (
                <div className="relative h-[25px] w-[88px] max-[1079px]:h-[34px] max-[1079px]:w-full max-[1079px]:max-w-[121px] min-[1230px]:h-[28px] min-[1230px]:w-[100px] min-[1430px]:aspect-[940/268] min-[1430px]:h-auto min-[1430px]:w-full min-[1430px]:max-h-[34px]">
                  <Image
                    src={profile.logo_2!}
                    alt={profile.logo_2_alt || ""}
                    fill
                    className="object-contain"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

/** Zod's optional strings reject `null`; APIs/DB often send null for empty fields. */
function deepNullToUndefined<T>(input: T): T {
  if (input === null) return undefined as T;
  if (Array.isArray(input)) return input.map(deepNullToUndefined) as T;
  if (typeof input === "object" && input !== null) {
    const o = input as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(o)) {
      out[k] = deepNullToUndefined(o[k]);
    }
    return out as T;
  }
  return input;
}

function normalizeVisionariesInputForZod(raw: unknown): unknown {
  const d = deepNullToUndefined(raw);
  if (d == null || typeof d !== "object") return d;
  const o = { ...(d as Record<string, unknown>) };
  if (o.categories != null && !Array.isArray(o.categories)) o.categories = [];
  if (o.pool_profiles != null && !Array.isArray(o.pool_profiles)) {
    o.pool_profiles = [];
  }
  if (Array.isArray(o.categories)) {
    o.categories = o.categories.map((cat: unknown) => {
      if (cat == null || typeof cat !== "object") return { profiles: [] };
      const c = cat as Record<string, unknown>;
      const prof = c.profiles;
      const items = c.items;
      const profiles = Array.isArray(prof)
        ? prof
        : Array.isArray(items)
          ? items
          : [];
      return { ...c, profiles };
    });
  }
  if (Array.isArray(o.pool_profiles)) {
    o.pool_profiles = o.pool_profiles.filter(
      (p): p is Record<string, unknown> => p != null && typeof p === "object",
    );
  }
  if (o.backvariant != null && typeof o.backvariant !== "string") {
    o.backvariant = String(o.backvariant);
  }
  return o;
}

const Visionaries1 = ({ data }: { data: any }) => {
  const actualData = data?.data ?? data;
  if (actualData == null || typeof actualData !== "object") {
    return null;
  }
  const parsed = Visionaries1DataSchema.safeParse(
    normalizeVisionariesInputForZod(actualData),
  );

  if (!parsed.success) {
    return null;
  }

  const parsedData = parsed.data;
  if (parsedData.status === false || parsedData.status === 0) {
    return null;
  }

  const isAnimationEnabled = parsedData.enable_animation;

  const isMobile = useIsMobile(640);
  /** Matches Visionaries layout: carousel mode switches at min-width 1080px in CSS. */
  const isBelowDesktopCarousel = useIsMobile(1080);
  const [layout, setLayout] = useState<LayoutTier>("sm");
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollMetrics, setScrollMetrics] = useState({
    scrollLeft: 0,
    scrollWidth: 1,
    clientWidth: 1,
  });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const update = () => setLayout(getLayoutTier(window.innerWidth));
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const useCategoryTabsLayout = parsedData.use_categories !== false;

  /** Real category tabs only (not the uncategorized bucket). */
  const tabCategories = useMemo(() => {
    const list = [...(parsedData.categories || [])].filter(
      (c) => c.is_active !== false && c.is_active !== 0
    );
    return list
      .filter((c) => !c.is_uncategorized_bucket && Number(c.id) !== -1)
      .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
  }, [parsedData.categories]);

  const poolProfiles = useMemo(() => {
    const fromPool = parsedData.pool_profiles ?? [];
    if (fromPool.length > 0) {
      return normalizeProfiles({
        order_index: 0,
        name: "",
        profiles: fromPool,
      } as VisionariesCategory);
    }
    const bucket = (parsedData.categories || []).find(
      (c) => c.is_uncategorized_bucket || Number(c.id) === -1
    );
    return normalizeProfiles(bucket);
  }, [parsedData.pool_profiles, parsedData.categories]);

  const showCategoryTabs =
    useCategoryTabsLayout && tabCategories.length > 0;

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null
  );

  const tabCategoryIdsKey = tabCategories
    .map((c) => String(c.id ?? ""))
    .join(",");

  useEffect(() => {
    setSelectedCategoryId(null);
  }, [parsedData.id, tabCategoryIdsKey]);

  const profiles = useMemo(() => {
    if (!useCategoryTabsLayout) {
      return poolProfiles;
    }
    if (selectedCategoryId === null) {
      return poolProfiles;
    }
    const cat = tabCategories.find(
      (c) => Number(c.id) === Number(selectedCategoryId)
    );
    return normalizeProfiles(cat);
  }, [
    useCategoryTabsLayout,
    selectedCategoryId,
    tabCategories,
    poolProfiles,
  ]);

  /**
   * 1080px ≤ width < 1750: fewer than 4 cards — centered row, no horizontal scroll or nav.
   * (Matches pool vs. category: same rules for any `profiles` list.)
   */
  const desktopLtFourCardsStatic =
    profiles.length > 0 && profiles.length < 4;
  /** XL (≥1750): fewer than 5 cards — same static row; slider only when ≥5. */
  const xlLtFiveCardsStatic =
    profiles.length > 0 && profiles.length < 5;
  /** XL + five or more cards: full-viewport-width strip with section-matching left inset. */
  const xlCarouselViewportBreakout = profiles.length > 4;

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollLeft = el.scrollLeft;
    const scrollWidth = el.scrollWidth;
    const clientWidth = el.clientWidth;
    const maxScroll = scrollWidth - clientWidth;
    setScrollMetrics((prev) => {
      if (
        prev.scrollLeft === scrollLeft &&
        prev.scrollWidth === scrollWidth &&
        prev.clientWidth === clientWidth
      ) {
        return prev;
      }
      return { scrollLeft, scrollWidth, clientWidth };
    });
    const nextPrev = scrollLeft > 2;
    const nextNext = scrollLeft < maxScroll - 2;
    setCanPrev((p) => (p === nextPrev ? p : nextPrev));
    setCanNext((p) => (p === nextNext ? p : nextNext));
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: 0, behavior: "auto" });
    updateScrollState();
  }, [selectedCategoryId, profiles.length, layout, updateScrollState]);

  /** After XL full-bleed layout paints, snap can leave scroll at the wrong offset; one rAF pass, no smooth scroll. */
  useEffect(() => {
    if (!xlCarouselViewportBreakout || layout !== "xl") return;
    const el = scrollRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      el.scrollTo({ left: 0, behavior: "auto" });
      updateScrollState();
    });
    return () => cancelAnimationFrame(id);
  }, [layout, xlCarouselViewportBreakout, updateScrollState]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    const ro = new ResizeObserver(() => updateScrollState());
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      ro.disconnect();
    };
  }, [profiles.length, updateScrollState]);

  useEffect(() => {
    updateScrollState();
  }, [layout, updateScrollState]);

  const bgStyle = formatBackgroundStyle(
    parsedData,
    Boolean(isMobile),
    parsedData.desktop_background_color || "#0A0A0A"
  );

  const trackColor =
    parsedData.progress_track_color || "rgba(255,255,255,0.2)";
  const fillColor = parsedData.progress_fill_color || "#FFFFFF";

  const scrollStep =
    layout === "xl"
      ? SCROLL_STEP_XL
      : layout === "lg"
        ? SCROLL_STEP_LG
        : layout === "md"
          ? SCROLL_STEP_MD
          : layout === "sm"
            ? SCROLL_STEP_SM
            : SCROLL_STEP_XS;

  const scrollByDir = (dir: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    // Advance by the real distance between two adjacent cards (card width + gap),
    // measured from the DOM, so navigation lands exactly on a snap point. On narrow
    // phones each card is `w-full` (= the viewport width), which is smaller than the
    // design constant CARD_WIDTH_XS, so a hardcoded step overshoots and leaves the
    // next card peeking in from the right. `offsetLeft` differences cancel out the
    // container's padding, and match the fixed constants on desktop.
    const cards = el.children;
    const measured =
      cards.length >= 2
        ? (cards[1] as HTMLElement).offsetLeft -
          (cards[0] as HTMLElement).offsetLeft
        : 0;
    const stride = measured > 0 ? measured : scrollStep;
    el.scrollBy({ left: dir * stride, behavior: "smooth" });
  };

  const trackW =
    layout === "xl" || layout === "lg" || layout === "md"
      ? PROGRESS_TRACK_PX_WIDE
      : layout === "sm"
        ? PROGRESS_TRACK_PX_SM
        : PROGRESS_TRACK_PX_XS;
  const { scrollLeft, scrollWidth, clientWidth } = scrollMetrics;
  const maxScroll = Math.max(scrollWidth - clientWidth, 0);
  const thumbCap = layout === "base" ? PROGRESS_THUMB_PX_XS : PROGRESS_THUMB_PX;
  const thumbW = Math.min(thumbCap, trackW);
  const thumbTravel = Math.max(trackW - thumbW, 0);
  const thumbLeft =
    maxScroll > 0 ? (scrollLeft / maxScroll) * thumbTravel : 0;

  const useMobileImage = isMobile === true;

  const coverflowItems = useMemo((): CarouselItem[] => {
    const transparentPixel =
      "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
    return profiles.map((profile, i) => {
      const image =
        useMobileImage && profile.mobile_image
          ? profile.mobile_image
          : profile.desktop_image || profile.mobile_image || "";
      const logo1 = profile.logo_1?.trim();
      const logo2 = profile.logo_2?.trim();
      const primaryIsLogo1 = Boolean(logo1);
      const logo = primaryIsLogo1 ? logo1! : logo2 || transparentPixel;
      const logoAlt = primaryIsLogo1
        ? profile.logo_1_alt || ""
        : profile.logo_2_alt || "";
      const logo2Out =
        primaryIsLogo1 && logo2 ? logo2 : undefined;
      const logo2AltOut =
        primaryIsLogo1 && logo2 ? profile.logo_2_alt : undefined;
      const logosLayout =
        profile.logos_layout === "stack" ? ("stack" as const) : ("row" as const);
      return {
        id: String(profile.id ?? `visionary-${i}`),
        image,
        name: profile.profile_name ?? "",
        designation: profile.profile_title ?? "",
        company: profile.company_name ?? "",
        logo,
        logoAlt,
        logo2: logo2Out,
        logo2Alt: logo2AltOut ?? undefined,
        logosLayout,
        subtitle: profile.profile_tagline,
        nameColor: profile.profile_name_color,
        designationColor: profile.profile_title_color,
      };
    });
  }, [profiles, useMobileImage]);

  const titleStart = (parsedData.title_start_color || "#FFFFFF").toUpperCase();
  const titleEnd = (parsedData.title_end_color || "#02A7B6").toUpperCase();
  const useRadialTitle =
    titleStart === "#FFFFFF" && titleEnd === "#02A7B6";

  /**
   * Coverflow only on desktop (width ≥ 1080px). Below that, always use the legacy horizontal strip.
   * When `isBelowDesktopCarousel` is still `null` (SSR/hydration), treat as narrow so Coverflow does not flash on phones.
   */
  const useCoverflowCarousel =
    parsedData.enable_coverflow_carousel && isBelowDesktopCarousel === false;

  /** Coverflow: section has no horizontal padding so carousel can bleed; header uses `px-9` (36px). Legacy: unchanged section insets. */
  const useCoverflowCarouselLayout = useCoverflowCarousel;

  return (
    <section
      className={cn(
        "relative flex w-full flex-col items-stretch overflow-x-visible",
        useCoverflowCarouselLayout
          ? cn(
              "gap-8 max-[1079px]:gap-6 sm:gap-10 md:gap-10 lg:gap-[60px]",
              "px-0",
              "max-[1079px]:py-10 sm:py-10 md:py-[60px] lg:py-[60px] xl:py-[80px]"
            )
          : cn(
              "gap-8",
              "sm:gap-10 sm:px-[60px] sm:py-10",
              "md:gap-10 md:px-[80px] md:py-[60px]",
              "lg:gap-[60px] lg:px-[100px] lg:py-[60px]",
              "xl:px-[160px] xl:py-[80px]",
              "max-[1079px]:gap-6 max-[1079px]:px-5 max-[1079px]:py-10"
            )
      )}
      style={bgStyle}
    >
      <div
        className={cn(
          "mx-auto flex w-full max-w-[1470px] flex-col gap-5",
          useCoverflowCarouselLayout && "px-9",
          "md:gap-6",
          "lg:gap-6",
          "max-[1079px]:gap-6"
        )}
      >
        <div
          className={cn(
            "flex w-full flex-col gap-0.5 sm:max-w-[370px] min-[1080px]:max-w-none",
            "max-[1079px]:max-w-[400px] max-[1079px]:gap-1"
          )}
        >
          {parsedData.pre_title ? (
            <RevealOnScroll enableAnimation={isAnimationEnabled}>
            <p
              className={cn(
                "w-full font-semibold leading-normal text-white/80",
                "sm:text-[22px] md:text-[26px] lg:text-[30px]",
                "max-[1079px]:text-2xl"
              )}
              style={{
                color: parsedData.pre_title_color || "rgba(255,255,255,0.8)",
                fontFamily: "var(--font-google-sans)",
              }}
            >
              {parsedData.pre_title}
            </p>
            </RevealOnScroll>
          ) : null}
          {parsedData.title ? (
            <RevealOnScroll enableAnimation={isAnimationEnabled}>
            <p
              className={cn(
                "w-full leading-[1.2]",
                "min-[1430px]:text-nowrap md:w-[460px]",
                useRadialTitle
                  ? "gradient-title-denton !text-[40px] sm:!text-[46px] md:!text-[56px] lg:!text-[64px] max-[1079px]:!text-[40px]"
                  : cn(
                      "bg-clip-text font-bold text-transparent sm:text-[46px] md:text-[56px] lg:text-[64px]",
                      "max-[1079px]:text-[40px]"
                    )
              )}
              style={
                useRadialTitle
                  ? undefined
                  : {
                      fontFamily: "var(--font-merriweather)",
                      backgroundImage: `linear-gradient(${parsedData.title_gradient_angle || "90deg"}, ${parsedData.title_start_color || "#FFFFFF"}, ${parsedData.title_end_color || "#02A7B6"})`,
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      color: "transparent",
                    }
              }
            >
              {parsedData.title}
            </p>
            </RevealOnScroll>
          ) : null}
        </div>

        {showCategoryTabs ? (
          <RevealOnScroll enableAnimation={isAnimationEnabled}>
          <div
            className={cn(
              "flex w-full flex-wrap gap-3 sm:gap-3 md:gap-3",
              "max-[1079px]:flex-nowrap max-[1079px]:overflow-x-auto max-[1079px]:pb-0.5 max-[1079px]:[scrollbar-width:none] max-[1079px]:[&::-webkit-scrollbar]:hidden"
            )}
            role="tablist"
            aria-label="Visionary categories"
          >
            {tabCategories.map((cat, idx) => {
              const cid =
                cat.id !== undefined && cat.id !== null ? Number(cat.id) : NaN;
              const selected =
                selectedCategoryId !== null &&
                !Number.isNaN(cid) &&
                selectedCategoryId === cid;
              const baseBorder = cat.border_color || "rgba(255,255,255,0.4)";
              const baseText = cat.text_color || "#FFFFFF";
              const baseBg = cat.background_color || "transparent";
              const actBorder = cat.active_border_color || "#02A7B6";
              const actText = resolveActiveTabTextColor(cat.active_text_color);
              const actBg = cat.active_background_color || "#02A7B6";
              return (
                <button
                  key={cat.id ?? idx}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  data-cta-name="category_tab"
                  className={cn(
                    "flex items-center justify-center rounded-full border px-4 py-2.5 text-sm leading-[1.49] transition-colors",
                    "border-[0.6px] sm:rounded-[40px] sm:py-2.5 sm:text-sm",
                    "md:px-4 md:py-2.5 md:text-base",
                    selected ? "font-semibold" : "font-medium",
                    "max-[1079px]:shrink-0 max-[1079px]:text-base",
                    selected
                      ? "max-[1079px]:rounded-[40px]"
                      : "max-[1079px]:rounded-[70px]"
                  )}
                  style={
                    selected
                      ? {
                          backgroundColor: actBg,
                          borderColor: actBorder,
                          color: actText,
                          fontFamily: "var(--font-google-sans)",
                        }
                      : {
                          backgroundColor: baseBg,
                          borderColor: baseBorder,
                          color: baseText,
                          fontFamily: "var(--font-google-sans)",
                        }
                  }
                  onClick={() => {
                    if (Number.isNaN(cid)) return;
                    setSelectedCategoryId((prev) =>
                      prev === cid ? null : cid
                    );
                  }}
                >
                  {cat.name || ""}
                </button>
              );
            })}
          </div>
          </RevealOnScroll>
        ) : null}
      </div>

      {profiles.length > 0 &&
      (!parsedData.enable_coverflow_carousel || isBelowDesktopCarousel !== false) ? (
        <RevealOnScroll
          y={0}
          duration={1000}
          enableAnimation={isAnimationEnabled}
        >
          <div
            className={cn(
              xlCarouselViewportBreakout
                ? cn(
                    "max-[1749px]:contents",
                    "min-[1750px]:flex min-[1750px]:w-[calc(100%+320px)] min-[1750px]:-mx-[160px] min-[1750px]:shrink-0 min-[1750px]:flex-col min-[1750px]:gap-8 min-[1750px]:sm:gap-10"
                  )
                : "contents"
            )}
          >
            <div
              className={cn(
                "max-[1079px]:mx-auto max-[1079px]:w-full max-[1079px]:max-w-[min(335px,100%)]",
                "min-[1080px]:max-[1749px]:contents",
                xlCarouselViewportBreakout
                  ? "min-[1750px]:block min-[1750px]:w-full"
                  : "min-[1750px]:contents"
              )}
            >
              <div
                ref={scrollRef}
                className={cn(
                  "mx-auto flex w-full max-w-[1470px] flex-row flex-nowrap gap-5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
                  "snap-x snap-mandatory",
                  "max-[1079px]:pr-5",
                  "min-[1080px]:pr-10",
                  "min-[1750px]:gap-10",
                  desktopLtFourCardsStatic &&
                    "min-[1080px]:max-[1749px]:snap-none min-[1080px]:max-[1749px]:justify-center min-[1080px]:max-[1749px]:overflow-x-visible min-[1080px]:max-[1749px]:pb-0",
                  xlLtFiveCardsStatic &&
                    "min-[1750px]:snap-none min-[1750px]:justify-center min-[1750px]:overflow-x-visible min-[1750px]:pb-0",
                  xlCarouselViewportBreakout &&
                    cn(
                      "min-[1750px]:mx-0 min-[1750px]:max-w-none min-[1750px]:w-full min-[1750px]:pl-[160px] min-[1750px]:pr-14",
                      "min-[1750px]:snap-none"
                    ),
                  "max-[1079px]:max-w-full max-[1079px]:gap-5 max-[1079px]:scroll-smooth max-[1079px]:overscroll-x-contain max-[1079px]:[-webkit-overflow-scrolling:touch]"
                )}
              >
                {profiles.map((profile, i) => {
                  const img =
                    useMobileImage && profile.mobile_image
                      ? profile.mobile_image
                      : profile.desktop_image || profile.mobile_image || "";
                  return (
                    <ProfileCard
                      key={profile.id ?? i}
                      profile={profile}
                      imageUrl={img}
                      className={cn(
                        "max-[1079px]:snap-center max-[1079px]:snap-always min-[1080px]:snap-start",
                        desktopLtFourCardsStatic &&
                          "min-[1080px]:max-[1749px]:snap-none",
                        xlLtFiveCardsStatic && "min-[1750px]:snap-none",
                        xlCarouselViewportBreakout && "min-[1750px]:snap-none"
                      )}
                    />
                  );
                })}
              </div>
            </div>

            <div
              className={cn(
                "mx-auto flex w-full max-w-[1470px] items-center justify-center gap-4",
                "md:max-w-[418px] md:gap-5",
                "max-[1079px]:mt-5 max-[1079px]:max-w-[335px] max-[1079px]:gap-5 max-[1079px]:self-center",
                desktopLtFourCardsStatic && "min-[1080px]:max-[1749px]:hidden",
                xlLtFiveCardsStatic && "min-[1750px]:hidden",
                xlCarouselViewportBreakout &&
                  "min-[1750px]:mx-0 min-[1750px]:max-w-none min-[1750px]:w-full min-[1750px]:pl-[160px] min-[1750px]:md:max-w-none"
              )}
            >
              <button
                type="button"
                aria-label="Previous profiles"
                data-cta-name="carousel_nav"
                disabled={!canPrev}
                onClick={() => scrollByDir(-1)}
                className={cn(
                  "flex size-10 shrink-0 items-center justify-center rounded-[80px] border border-white bg-white/20 p-2 text-white transition-opacity",
                  "max-[1079px]:border-white max-[1079px]:bg-transparent max-[1079px]:p-2.5",
                  "md:size-11 md:p-2.5",
                  !canPrev && "pointer-events-none opacity-40"
                )}
              >
                {parsedData.left_navigation_icon ? (
                  <Image
                    src={parsedData.left_navigation_icon}
                    alt=""
                    width={24}
                    height={24}
                    className={cn(
                      "size-[20.5px] object-contain md:size-6",
                      "max-[1079px]:size-6"
                    )}
                  />
                ) : (
                  <ChevronLeft
                    className={cn(
                      "size-[20.5px] md:size-6",
                      "max-[1079px]:size-6"
                    )}
                    strokeWidth={2}
                  />
                )}
              </button>

              <div
                className={cn(
                  "relative h-[5px] w-[min(100%,248px)] shrink-0 overflow-hidden rounded-[8.5px] sm:w-[248px]",
                  "max-[1079px]:h-[6px] max-[1079px]:w-[207px] max-[1079px]:rounded-[10px]",
                  "md:h-[6px] md:w-[290px] md:rounded-[10px]"
                )}
                style={{ backgroundColor: trackColor }}
                aria-hidden
              >
                <div
                  className={cn(
                    "absolute left-0 top-0 h-full rounded-[8.5px]",
                    "max-[1079px]:rounded-[10px]",
                    "md:rounded-[10px]"
                  )}
                  style={{
                    width: `${thumbW}px`,
                    transform: `translateX(${thumbLeft}px)`,
                    backgroundColor: fillColor,
                  }}
                />
              </div>

              <button
                type="button"
                aria-label="Next profiles"
                data-cta-name="carousel_nav"
                disabled={!canNext}
                onClick={() => scrollByDir(1)}
                className={cn(
                  "flex size-10 shrink-0 items-center justify-center rounded-[80px] border border-white/85 bg-white/20 p-2 text-white transition-opacity",
                  "max-[1079px]:border-white max-[1079px]:bg-transparent max-[1079px]:p-2.5",
                  "md:size-11 md:p-2.5",
                  !canNext && "pointer-events-none opacity-40"
                )}
              >
                {parsedData.right_navigation_icon ? (
                  <Image
                    src={parsedData.right_navigation_icon}
                    alt=""
                    width={24}
                    height={24}
                    className={cn(
                      "size-[20.5px] object-contain md:size-6",
                      "max-[1079px]:size-6"
                    )}
                  />
                ) : (
                  <ChevronRight
                    className={cn(
                      "size-[20.5px] md:size-6",
                      "max-[1079px]:size-6"
                    )}
                    strokeWidth={2}
                  />
                )}
              </button>
            </div>
          </div>
        </RevealOnScroll>
      ) : null}

      {profiles.length > 0 && useCoverflowCarousel ? (
        <div className="w-full overflow-x-clip px-[36px] pb-10 sm:pb-12">
          <CoverflowCarousel
            items={coverflowItems}
            autoplay={parsedData.coverflow_autoplay === true}
            delay={parsedData.coverflow_autoplay_delay ?? 4000}
            loop={parsedData.coverflow_loop !== false}
            fullBleedRight
            className="w-full"
          />
        </div>
      ) : null}
    </section>
  );
};

export default memo(Visionaries1);
